import axios from 'axios';
import * as THREE from 'three';
import {
  CabinetCoverPos,
  CabinetTrimPos,
  InsideType,
} from '../../../constants/enum';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';
import { nearlyEqual } from '../../../utils/geometry/epsilon';
import { has } from 'lodash';

export default class CreateCabinetTrim {
  constructor(sceneManager) {
    this.sceneManager = sceneManager;
    this.proximityThreshold = 0.01; // Giảm để chỉ nhận diện khi thực sự sát nhau
    this.rotationTolerance = 0.01; // Tolerance cho góc quay (radian)
    this.alignmentTolerance = 0.01; // Tolerance cho thẳng hàng
    this.sizeTolerance = 0.01; // Tolerance cho kích thước mặt tiếp xúc
    this.lstTopCabinetTrim = new Map();
    this.lstEdgeCabinetTrim = new Map();
    this.moduleGroups = new Map();
    this.planePadding = 0.3;
    this.planeHeight = 0.02; // Độ dày mặt bếp
    this.widthCabinetTrim = 0.12; // Kích thước rộng nẹp
    this.heightCabinetTrim = 0.03; // Kích thước cao nẹp
    this.thickness = 0.017; //Độ dày ván
    this.countertopOverhang = 0.02; // Phần nhô ra của mặt bếp so với module
    this.cabinetTrimColor = 0xff0000; // Màu nẹp khi chưa có vật liệu
    this.hiddenCountertops = new Set(); // Lưu trữ các countertop đã ẩn
    this.textureCache = new Map();
    this.textureLoader = new THREE.TextureLoader();
  }

  updateCabinetTrimSettings(newCbTrim) {
    this.widthCabinetTrim = newCbTrim.width;
    this.heightCabinetTrim = newCbTrim.height;
  }

  // async loadTexture(url, rotation = 0) {
  //   if (this.textureCache.has(url)) {
  //     return this.textureCache.get(url);
  //   }

  //   return await axios({
  //     method: 'get',
  //     url,
  //     responseType: 'blob',
  //     headers: {
  //       'Cache-Control': 'no-cache',
  //       Pragma: 'no-cache',
  //     },
  //   })
  //     .then(response => {
  //       const imageUrl = URL.createObjectURL(response.data);

  //       return new Promise((resolve, reject) => {
  //         this.textureLoader.load(
  //           imageUrl,
  //           texture => {
  //             URL.revokeObjectURL(imageUrl);

  //             texture.rotation = rotation;
  //             texture.center.set(0.5, 0.5);
  //             texture.needsUpdate = true;

  //             this.textureCache.set(url, texture);
  //             resolve(texture);
  //           },
  //           undefined,
  //           error => {
  //             URL.revokeObjectURL(imageUrl);
  //             reject(error);
  //           }
  //         );
  //       });
  //     })
  //     .catch(e => {
  //       console.log(e);
  //       throw e;
  //     });
  // }

  async loadTexture(url, rotation = 0, repeat = { x: 1, y: 1 }) {
    if (this.textureCache.has(url)) {
      return this.textureCache.get(url);
    }

    return await axios({
      method: 'get',
      url,
      responseType: 'blob',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    })
      .then(response => {
        const imageUrl = URL.createObjectURL(response.data);

        return new Promise((resolve, reject) => {
          this.textureLoader.load(
            imageUrl,
            texture => {
              URL.revokeObjectURL(imageUrl);

              texture.rotation = rotation;
              texture.center.set(0.5, 0.5);
              texture.repeat.set(repeat.x, repeat.y);
              texture.wrapS = THREE.RepeatWrapping;
              texture.wrapT = THREE.RepeatWrapping;
              texture.needsUpdate = true;

              this.textureCache.set(url, texture);
              resolve(texture);
            },
            undefined,
            error => {
              URL.revokeObjectURL(imageUrl);
              reject(error);
            }
          );
        });
      })
      .catch(e => {
        console.log(e);
        throw e;
      });
  }

  // Tìm tất cả modules trong scene
  getAllModules(excludeTypes = []) {
    const modules = [];
    this.sceneManager.scene.traverse(object => {
      if (object.userData?.type === 'module') {
        // Kiểm tra xem tên có chứa bất kỳ chuỗi nào trong mảng không
        const containsExcludedName = excludeTypes.some(type =>
          object.userData?.moduleType?.includes(type)
        );

        // Chỉ thêm vào nếu KHÔNG chứa bất kỳ tên nào trong mảng
        if (!containsExcludedName) {
          modules.push(object);
        }
      }
    });

    return modules;
  }

  getModuleById(moduleId) {
    return this.sceneManager.cabinetManager.getModuleByGltfUuid(moduleId);
  }

  // Lấy rotation chuẩn hóa của module (chỉ lấy Y rotation)
  getNormalizedRotation(module) {
    return module.rotation.y;
  }

  ////////////START - Kiểm tra va chạm
  checkCeiling(model1, model2) {
    if (!model1 || !model2 || model1 === model2) return false;

    model1.updateMatrixWorld(true);
    model2.updateMatrixWorld(true);

    const cloneModel1 = model1.clone();
    const cloneModel2 = model2.clone();

    cloneModel1.scale.set(1, 1.005, 1);

    // Nếu không có rotation, sử dụng AABB đơn giản
    const box1 = new THREE.Box3().setFromObject(cloneModel1);
    const box2 = new THREE.Box3().setFromObject(cloneModel2);

    let topCollision = false;

    if (box1.intersectsBox(box2) === true) {
      topCollision = true;
    }

    return {
      collision: box1.intersectsBox(box2),
      top: topCollision,
    };
  }

  checkCollisionOld(model1, model2, requireSameSize = false) {
    if (!model1 || !model2 || model1 === model2) return false;

    model1.updateMatrixWorld(true);
    model2.updateMatrixWorld(true);

    const cloneModel1 = model1.clone();
    const cloneModel2 = model2.clone();

    cloneModel1.scale.set(1.005, 1, 1.005);

    // Nếu không có rotation, sử dụng AABB đơn giản
    const box1 = new THREE.Box3().setFromObject(cloneModel1);
    const box2 = new THREE.Box3().setFromObject(cloneModel2);

    let leftCollision = false;
    let rightCollision = false;
    let backCollision = false;

    let isSameSize;

    if (box1.intersectsBox(box2) === true) {
      //Kiểm tra cụ thể là cạnh nào đang tiếp xúc
      const edges = this.getMeshesByName(cloneModel1);

      if (!edges.left || !edges.right || !edges.back) {
        return { collision: box1.intersectsBox(box2) };
      }

      const boxLeftEdge = new THREE.Box3().setFromObject(edges.left);
      const boxRightEdge = new THREE.Box3().setFromObject(edges.right);
      const boxBackEdge = new THREE.Box3().setFromObject(edges.back);

      //Kiểm tra xem mặt tiếp xúc có bằng nhau kh nếu có yêu cầu cùng size
      if (requireSameSize) {
        const md1 = model1.clone();
        const md2 = model2.clone();

        const md1Box = new THREE.Box3().setFromObject(
          GeometryUtils.moduleOnlyFrame(md1)
        );
        const sizeMd1 = md1Box.getSize(new THREE.Vector3());

        const md2Box = new THREE.Box3().setFromObject(
          GeometryUtils.moduleOnlyFrame(md2)
        );
        const sizeMd2 = md2Box.getSize(new THREE.Vector3());

        if (md1.userData.currentSize.depth === md2.userData.currentSize.depth) {
          isSameSize = true;
        } else {
          isSameSize = false;
        }
      }

      if (boxLeftEdge.intersectsBox(box2)) {
        if (requireSameSize && !isSameSize) {
          leftCollision = false;
        } else {
          leftCollision = true;
        }
      }
      if (boxRightEdge.intersectsBox(box2)) {
        if (requireSameSize && !isSameSize) {
          rightCollision = false;
        } else {
          rightCollision = true;
        }
      }
      if (boxBackEdge.intersectsBox(box2)) {
        backCollision = true;
      }
    }

    return {
      collision: box1.intersectsBox(box2),
      left: leftCollision,
      right: rightCollision,
      back: backCollision,
    };
  }

  checkCollision(model1, model2, requireSameSize = false) {
    if (!model1 || !model2 || model1 === model2) return false;

    const MIN_CONTACT_WIDTH = this.thickness + 0.001; // Độ dày ván cộng thêm trừ hao

    // Tính kích thước phần giao nhau giữa 2 box theo từng trục
    const getIntersectionSize = (boxA, boxB) => {
      const minX = Math.max(boxA.min.x, boxB.min.x);
      const maxX = Math.min(boxA.max.x, boxB.max.x);
      const minY = Math.max(boxA.min.y, boxB.min.y);
      const maxY = Math.min(boxA.max.y, boxB.max.y);
      const minZ = Math.max(boxA.min.z, boxB.min.z);
      const maxZ = Math.min(boxA.max.z, boxB.max.z);

      const x = Math.max(0, maxX - minX);
      const y = Math.max(0, maxY - minY);
      const z = Math.max(0, maxZ - minZ);

      //Giá trị thấp nhất là mặt tiếp xúc, 2 giá trị còn lại là kích thước mặt tiếp xúc
      const sorted = [x, y, z].sort((a, b) => a - b);

      return sorted[1]; //Cạnh nhỏ nhất của mặt tiếp xúc để xác định tiếp xúc ở mặt nào của module
    };

    model1.updateMatrixWorld(true);
    model2.updateMatrixWorld(true);

    const cloneModel1 = model1.clone();
    const cloneModel2 = model2.clone();

    cloneModel1.scale.set(1.005, 1, 1.005);

    // Nếu không có rotation, sử dụng AABB đơn giản
    const box1 = new THREE.Box3().setFromObject(cloneModel1);
    const box2 = new THREE.Box3().setFromObject(cloneModel2);

    let leftCollision = false;
    let rightCollision = false;
    let backCollision = false;

    let isSameSize;

    if (box1.intersectsBox(box2) === true) {
      //Kiểm tra cụ thể là cạnh nào đang tiếp xúc
      const edges = this.getMeshesByName(cloneModel1);

      if (!edges.left || !edges.right || !edges.back) {
        return { collision: box1.intersectsBox(box2) };
      }

      const boxLeftEdge = new THREE.Box3().setFromObject(edges.left);
      const boxRightEdge = new THREE.Box3().setFromObject(edges.right);
      const boxBackEdge = new THREE.Box3().setFromObject(edges.back);

      //Kiểm tra xem mặt tiếp xúc có bằng nhau kh nếu có yêu cầu cùng size
      if (requireSameSize) {
        const md1 = model1.clone();
        const md2 = model2.clone();

        const md1Box = new THREE.Box3().setFromObject(
          GeometryUtils.moduleOnlyFrame(md1)
        );
        const sizeMd1 = md1Box.getSize(new THREE.Vector3());

        const md2Box = new THREE.Box3().setFromObject(
          GeometryUtils.moduleOnlyFrame(md2)
        );
        const sizeMd2 = md2Box.getSize(new THREE.Vector3());

        if (md1.userData.currentSize.depth === md2.userData.currentSize.depth) {
          isSameSize = true;
        } else {
          isSameSize = false;
        }
      }

      if (boxLeftEdge.intersectsBox(box2)) {
        const overlap = getIntersectionSize(boxLeftEdge, box2);
        const hasEnoughContact = overlap > MIN_CONTACT_WIDTH;

        if ((requireSameSize && !isSameSize) || !hasEnoughContact) {
          leftCollision = false;
        } else {
          leftCollision = true;
        }
      }

      if (boxRightEdge.intersectsBox(box2)) {
        const overlap = getIntersectionSize(boxRightEdge, box2);
        const hasEnoughContact = overlap > MIN_CONTACT_WIDTH;

        if ((requireSameSize && !isSameSize) || !hasEnoughContact) {
          rightCollision = false;
        } else {
          rightCollision = true;
        }
      }

      if (boxBackEdge.intersectsBox(box2)) {
        const overlap = getIntersectionSize(boxBackEdge, box2);
        const hasEnoughContact = overlap > MIN_CONTACT_WIDTH;

        backCollision = hasEnoughContact;
      }
    }

    return {
      collision: box1.intersectsBox(box2),
      left: leftCollision,
      right: rightCollision,
      back: backCollision,
    };
  }

  // Cải thiện phương thức hasRotation để nhạy hơn
  hasRotation(object) {
    const rotation = object.rotation;
    const threshold = 0.001; // Threshold nhỏ hơn để detect rotation chính xác hơn
    return (
      Math.abs(rotation.x) > threshold ||
      Math.abs(rotation.y) > threshold ||
      Math.abs(rotation.z) > threshold
    );
  }

  isSeparatedOnAxis(corners1, corners2, axis) {
    // Project all corners onto the axis
    const projections1 = corners1.map(corner => corner.dot(axis));
    const projections2 = corners2.map(corner => corner.dot(axis));

    const min1 = Math.min(...projections1);
    const max1 = Math.max(...projections1);
    const min2 = Math.min(...projections2);
    const max2 = Math.max(...projections2);

    // Check if projections are separated
    return max1 < min2 || max2 < min1;
  }

  // Cải thiện phương thức checkOBBCollision
  checkOBBCollision(object1, object2) {
    const obb1 = this.getOrientedBoundingBox(object1);
    const obb2 = this.getOrientedBoundingBox(object2);

    if (!obb1 || !obb2) return false;

    // Sử dụng Separating Axis Theorem (SAT)
    // Kiểm tra tất cả các trục có thể tách biệt hai OBB
    const allAxes = [...obb1.axes, ...obb2.axes];

    // Thêm các trục cross product giữa các cạnh của hai OBB
    for (let i = 0; i < 3; i++) {
      for (let j = 0; j < 3; j++) {
        const crossAxis = new THREE.Vector3().crossVectors(
          obb1.axes[i],
          obb2.axes[j]
        );
        if (crossAxis.lengthSq() > 0.0001) {
          // Tránh trục có độ dài 0
          crossAxis.normalize();
          allAxes.push(crossAxis);
        }
      }
    }

    // Test separation trên mỗi trục
    for (const axis of allAxes) {
      if (this.isSeparatedOnAxis(obb1.corners, obb2.corners, axis)) {
        return false; // Tách biệt trên trục này, không có va chạm
      }
    }

    return true; // Không tìm thấy trục tách biệt, có va chạm
  }

  // Cập nhật phương thức getOrientedBoundingBox để chính xác hơn
  getOrientedBoundingBox(object) {
    // Đảm bảo world matrix được update
    object.updateMatrixWorld(true);

    // Lưu lại transform hiện tại của object
    const originalPosition = object.position.clone();
    const originalRotation = object.rotation.clone();
    const originalScale = object.scale.clone();

    // Tạm thời reset transform của object về identity để lấy local size
    object.position.set(0, 0, 0);
    object.rotation.set(0, 0, 0);
    object.scale.set(1, 1, 1);
    object.updateMatrixWorld(true);

    // Tạo bounding box trong local space (không bị ảnh hưởng bởi rotation)
    const localBox = new THREE.Box3().setFromObject(object);

    // Lấy kích thước thực trong local space
    const size = localBox.getSize(new THREE.Vector3());
    const localCenter = localBox.getCenter(new THREE.Vector3());

    // Khôi phục lại transform ban đầu
    object.position.copy(originalPosition);
    object.rotation.copy(originalRotation);
    object.scale.copy(originalScale);
    object.updateMatrixWorld(true);

    // Tạo BoxGeometry từ kích thước thực trong local space
    const boxGeometry = new THREE.BoxGeometry(size.x, size.y, size.z);

    // Lấy world matrix của object
    const worldMatrix = object.matrixWorld;

    // Tạo 8 góc của bounding box từ local size, nhưng centered tại local center
    const halfSize = size.clone().multiplyScalar(0.5);
    const corners = [
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z - halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z + halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y - halfSize.y,
        localCenter.z + halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x - halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z + halfSize.z
      ),
      new THREE.Vector3(
        localCenter.x + halfSize.x,
        localCenter.y + halfSize.y,
        localCenter.z + halfSize.z
      ),
    ];

    // Transform tất cả các góc theo world matrix
    corners.forEach(corner => corner.applyMatrix4(worldMatrix));

    // Tính toán các trục orientation từ rotation matrix
    const rotationMatrix = new THREE.Matrix4().extractRotation(worldMatrix);
    const axes = [
      new THREE.Vector3(1, 0, 0).applyMatrix4(rotationMatrix),
      new THREE.Vector3(0, 1, 0).applyMatrix4(rotationMatrix),
      new THREE.Vector3(0, 0, 1).applyMatrix4(rotationMatrix),
    ];

    return {
      corners: corners,
      center: object.getWorldPosition(new THREE.Vector3()),
      axes: axes,
      rotation: object.rotation.clone(),
      scale: object.scale.clone(),
      worldMatrix: worldMatrix.clone(),
      geometry: boxGeometry,
      size: size, // Kích thước thực trong local space
    };
  }

  //Kiểm tra module đang tiếp xúc với bức tường nào
  detectModuleWallCollision(module) {
    if (!this.sceneManager.walls || this.sceneManager.walls.length === 0)
      return false;

    // Tổng hợp kết quả va chạm từ tất cả các tường
    let hasCollision = false;
    let leftCollision = false;
    let rightCollision = false;
    let backCollision = false;

    //Lấy cả tường và cột
    const wallsToCheck = [
      ...this.sceneManager.walls,
      ...(this.sceneManager.columns || []),
    ];

    // Kiểm tra va chạm với từng tường
    // for (const wall of this.sceneManager.walls) {
    for (const wall of wallsToCheck) {
      const check = this.checkCollision(module, wall.mesh);

      if (check.collision) {
        hasCollision = true;

        // Tích lũy kết quả (OR logic)
        leftCollision = leftCollision || check.left;
        rightCollision = rightCollision || check.right;
        backCollision = backCollision || check.back;
      }
    }

    // Return false nếu không có va chạm
    if (!hasCollision) {
      return false;
    }

    // Return tổng hợp kết quả va chạm
    return {
      collision: true,
      left: leftCollision,
      right: rightCollision,
      back: backCollision,
    };
  }

  //Kiểm tra module đang tiếp xúc với module nào hay không
  detectModuleWithModuleCollision(module) {
    //Lấy tất cả module
    const allModules = this.sceneManager.scene.children.filter(
      child => child.userData?.type === 'module' && child !== module
    );

    // Tổng hợp kết quả va chạm từ tất cả các tường
    let hasCollision = false;
    let leftCollision = false;
    let rightCollision = false;
    let backCollision = false;

    // Kiểm tra va chạm với từng module khác
    for (const md of allModules) {
      const check = this.checkCollision(module, md);

      if (check.collision) {
        hasCollision = true;

        // Tích lũy kết quả (OR logic)
        leftCollision = leftCollision || check.left;
        rightCollision = rightCollision || check.right;
        backCollision = backCollision || check.back;
      }
    }

    // Return false nếu không có va chạm
    if (!hasCollision) {
      return false;
    }

    // Return tổng hợp kết quả va chạm
    return {
      collision: true,
      left: leftCollision,
      right: rightCollision,
      back: backCollision,
    };
  }

  //Kiểm tra module đang tiếp xúc với module nào CÙNG SIZE hay không
  detectModuleWithModuleSameSizeCollision(module) {
    //Lấy tất cả module
    const allModules = this.sceneManager.scene.children.filter(
      child => child.userData?.type === 'module' && child !== module
    );

    // Tổng hợp kết quả va chạm từ tất cả các tường
    let hasCollision = false;
    let leftCollision = false;
    let rightCollision = false;
    let backCollision = false;

    // Kiểm tra va chạm với từng module khác
    for (const md of allModules) {
      const requireSameSize = true;
      const check = this.checkCollision(module, md, requireSameSize);

      if (check.collision) {
        hasCollision = true;

        // Tích lũy kết quả (OR logic)
        leftCollision = leftCollision || check.left;
        rightCollision = rightCollision || check.right;
        backCollision = backCollision || check.back;
      }
    }

    // Return false nếu không có va chạm
    if (!hasCollision) {
      return false;
    }

    // Return tổng hợp kết quả va chạm
    return {
      collision: true,
      left: leftCollision,
      right: rightCollision,
      back: backCollision,
    };
  }

  detectCollidingModules(module, minOverlap = 0.017) {
    const allModules = this.sceneManager.scene.children.filter(
      child => child.userData?.type === 'module' && child !== module
    );

    const bbox1 = new THREE.Box3().setFromObject(module);
    const center1 = new THREE.Vector3();
    bbox1.getCenter(center1);

    for (const md of allModules) {
      const bbox2 = new THREE.Box3().setFromObject(md);

      const intersection = new THREE.Box3();
      intersection.copy(bbox1).intersect(bbox2);

      if (intersection.isEmpty()) continue;

      const overlapSize = new THREE.Vector3();
      intersection.getSize(overlapSize);

      const hasSignificantOverlap =
        Math.min(overlapSize.x, overlapSize.y, overlapSize.z) > minOverlap;

      if (!hasSignificantOverlap) continue;

      const center2 = new THREE.Vector3();
      bbox2.getCenter(center2);

      const dx = center2.x - center1.x;
      const dz = center2.z - center1.z;

      // Tìm thấy 1 module → trả về luôn
      return {
        module: md,
        collision: true,
        left: dx < 0,
        right: dx > 0,
        back: dz < 0,
        overlap: {
          x: overlapSize.x,
          y: overlapSize.y,
          z: overlapSize.z,
        },
      };
    }

    return null; // Không tìm thấy
  }

  // Hàm lấy mesh theo cạnh
  getMeshesByName(model) {
    const foundMeshes = {
      left: null,
      right: null,
      back: null,
    };

    if (model.userData.moduleType.includes('corner')) {
      return foundMeshes;
    }

    model.traverse(child => {
      if (child.isMesh) {
        const name = child.name;
        const has = str => name.includes(str);

        if (has('BIA-TRAI') || has('BIA-1') || has('G-BIA-1')) {
          foundMeshes.left = child;
        } else if (has('BIA-PHAI') || has('BIA-2') || has('G-BIA-2')) {
          foundMeshes.right = child;
        } else if (has('HAU') || has('G-HAU') || has('HAU-1')) {
          foundMeshes.back = child;
        }
      }
    });

    return foundMeshes;
  }

  ////////////END - Kiểm tra va chạm

  // Kiểm tra 2 module có cùng góc quay không
  hasSameRotation(module1, module2) {
    const rot1 = this.getNormalizedRotation(module1);
    const rot2 = this.getNormalizedRotation(module2);
    const diff = Math.abs(rot1 - rot2);

    // Xử lý trường hợp góc quay qua 2π
    const normalizedDiff = Math.min(diff, Math.abs(diff - 2 * Math.PI));

    return normalizedDiff <= this.rotationTolerance;
  }

  // Kiểm tra 2 module có thẳng hàng không (theo trục X hoặc Z)
  isAligned(module1, module2) {
    const pos1 = module1.position;
    const pos2 = module2.position;

    // Kiểm tra thẳng hàng theo trục X (cùng Z)
    const alignedX = Math.abs(pos1.z - pos2.z) <= this.alignmentTolerance;

    // Kiểm tra thẳng hàng theo trục Z (cùng X)
    const alignedZ = Math.abs(pos1.x - pos2.x) <= this.alignmentTolerance;

    return alignedX || alignedZ;
  }

  // Kiểm tra 2 module có cùng cao độ (theo trục Y)
  isSameHeight(module1, module2) {
    const pos1 = module1.position;
    const pos2 = module2.position;

    // Dung sai cho việc so sánh độ cao
    // const tolerance = this.alignmentTolerance || 0.001;
    const tolerance = 0.001;

    return Math.abs(pos1.y - pos2.y) <= tolerance;
  }

  // Tính kích thước mặt tiếp xúc giữa 2 module
  getContactFaceSize(module1, module2) {
    const box1 = new THREE.Box3().setFromObject(module1);
    const box2 = new THREE.Box3().setFromObject(module2);

    const pos1 = module1.position;
    const pos2 = module2.position;

    // Xác định hướng tiếp xúc
    const deltaX = Math.abs(pos1.x - pos2.x);
    const deltaZ = Math.abs(pos1.z - pos2.z);

    let contactSize1, contactSize2;

    if (deltaX > deltaZ) {
      // Tiếp xúc theo trục X (mặt YZ)
      const size1 = box1.max.clone().sub(box1.min);
      const size2 = box2.max.clone().sub(box2.min);
      contactSize1 = size1.y * size1.z;
      contactSize2 = size2.y * size2.z;
    } else {
      // Tiếp xúc theo trục Z (mặt XY)
      const size1 = box1.max.clone().sub(box1.min);
      const size2 = box2.max.clone().sub(box2.min);
      contactSize1 = size1.x * size1.y;
      contactSize2 = size2.x * size2.y;
    }

    return { size1: contactSize1, size2: contactSize2 };
  }

  // Kiểm tra kích thước mặt tiếp xúc có bằng nhau không
  hasEqualContactSize(module1, module2) {
    const module1WithoutAccessory =
      GeometryUtils.moduleWithoutAccessory(module1);
    const module2WithoutAccessory =
      GeometryUtils.moduleWithoutAccessory(module2);

    const { size1, size2 } = this.getContactFaceSize(
      module1WithoutAccessory,
      module2WithoutAccessory
    );
    const sizeDiff = Math.abs(size1 - size2);
    const avgSize = (size1 + size2) / 2;

    // So sánh theo tỷ lệ phần trăm
    return sizeDiff / avgSize <= this.sizeTolerance;
  }

  // Tính khoảng cách giữa 2 bounding box
  calculateBoxToBoxDistance(box1, box2) {
    const dx = Math.max(box1.min.x - box2.max.x, 0, box2.min.x - box1.max.x);
    const dy = Math.max(box1.min.y - box2.max.y, 0, box2.min.y - box1.max.y);
    const dz = Math.max(box1.min.z - box2.max.z, 0, box2.min.z - box1.max.z);

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  // Kiểm tra xem 2 module có thể nhóm được không (tất cả điều kiện)
  canModulesGroup(module1, module2) {
    // 1. Kiểm tra khoảng cách gần nhau
    const box1 = new THREE.Box3().setFromObject(module1);
    const box2 = new THREE.Box3().setFromObject(module2);
    const distance = this.calculateBoxToBoxDistance(box1, box2);

    if (distance > this.proximityThreshold) {
      return false;
    }

    // 2. Kiểm tra cùng góc quay
    if (!this.hasSameRotation(module1, module2)) {
      return false;
    }

    // 3. Kiểm tra thẳng hàng
    if (!this.isAligned(module1, module2)) {
      return false;
    }

    // 4. Kiểm tra cùng cao độ
    if (!this.isSameHeight(module1, module2)) {
      return false;
    }

    // 5. Kiểm tra kích thước mặt tiếp xúc
    if (!this.hasEqualContactSize(module1, module2)) {
      return false;
    }

    return true;
  }

  // Tìm các nhóm modules bằng Union-Find algorithm để đảm bảo tính liên thông
  findModuleGroups(modules, area) {
    const groups = [];
    const visited = new Set();

    modules.forEach(module => {
      if (module.userData.moduleData?.moduleType?.includes(area)) {
        if (!visited.has(module.uuid)) {
          const group = [];
          this.dfsGroupSearch(module, modules, group, visited);
          if (group.length > 1) {
            // Chỉ tạo group khi có nhiều hơn 1 module
            groups.push(group);
          }
        }
      }
    });

    return groups;
  }

  // Tìm các nhóm modules đụng tới trần
  findCeilingModuleGroups(modules) {
    const groups = [];
    const visited = new Set();

    modules.forEach(module => {
      if (module.userData.cabinetTrimPos?.top === true) {
        if (!visited.has(module.uuid)) {
          const group = [];
          this.dfsGroupSearch(module, modules, group, visited);
          // if (group.length > 1) {
          //   // Chỉ tạo group khi có nhiều hơn 1 module
          //   groups.push(group);
          // }
          groups.push(group);
        }
      }
    });

    return groups;
  }

  // Tìm kiếm theo chiều sâu với điều kiện nghiêm ngặt
  dfsGroupSearch(currentModule, allModules, group, visited) {
    visited.add(currentModule.uuid);
    group.push(currentModule);

    allModules.forEach(otherModule => {
      if (
        !visited.has(otherModule.uuid) &&
        this.canModulesGroup(currentModule, otherModule)
      ) {
        this.dfsGroupSearch(otherModule, allModules, group, visited);
      }
    });
  }

  // Xác thực group sau khi tìm được (đảm bảo tất cả modules trong group đều thỏa mãn điều kiện với nhau)
  validateGroup(moduleGroup) {
    //Những group có 1 module thì mặc định đúng
    if (moduleGroup.length < 2) return true;

    // Kiểm tra mọi cặp module trong group
    for (let i = 0; i < moduleGroup.length - 1; i++) {
      for (let j = i + 1; j < moduleGroup.length; j++) {
        // Kiểm tra cùng rotation và alignment với module đầu tiên
        if (
          !this.hasSameRotation(moduleGroup[i], moduleGroup[0]) ||
          !this.hasSameRotation(moduleGroup[j], moduleGroup[0])
        ) {
          return false;
        }
      }
    }

    // Kiểm tra tính liên thông của group (mỗi module phải kề với ít nhất 1 module khác)
    return this.isGroupConnected(moduleGroup);
  }

  // Kiểm tra tính liên thông của group
  isGroupConnected(moduleGroup) {
    if (moduleGroup.length < 2) return false;

    // Tạo graph adjacency
    const adjacency = new Map();
    moduleGroup.forEach(module => {
      adjacency.set(module.uuid, new Set());
    });

    // Tạo kết nối giữa các module
    for (let i = 0; i < moduleGroup.length; i++) {
      for (let j = i + 1; j < moduleGroup.length; j++) {
        if (this.canModulesGroup(moduleGroup[i], moduleGroup[j])) {
          adjacency.get(moduleGroup[i].uuid).add(moduleGroup[j].uuid);
          adjacency.get(moduleGroup[j].uuid).add(moduleGroup[i].uuid);
        }
      }
    }

    // BFS để kiểm tra liên thông
    const visited = new Set();
    const queue = [moduleGroup[0].uuid];
    visited.add(moduleGroup[0].uuid);

    while (queue.length > 0) {
      const current = queue.shift();
      const neighbors = adjacency.get(current);

      neighbors.forEach(neighbor => {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      });
    }

    return visited.size === moduleGroup.length;
  }

  // Tính bounding box tổng hợp của một nhóm modules
  calculateGroupBoundingBox(moduleGroup, withPadding = false) {
    const combinedBBox = new THREE.Box3();

    moduleGroup.forEach(module => {
      const moduleBBox = new THREE.Box3().setFromObject(module);
      combinedBBox.union(moduleBBox);
    });

    if (withPadding) {
      const padding = new THREE.Vector3(
        this.planePadding,
        0,
        this.planePadding
      );
      combinedBBox.expandByVector(padding);
    }

    return combinedBBox;
  }

  removeAccessoriesFromModelGroup(moduleGroup) {
    return moduleGroup
      .map(group => {
        // Tạo group mới để không ảnh hưởng đến group gốc
        const filteredGroup = GeometryUtils.moduleOnlyFrame(group);

        return filteredGroup;
      })
      .filter(group => {
        // Kiểm tra xem group còn children không (loại bỏ group rỗng)
        let hasNonSinkChildren = false;
        group.traverse(object => {
          if (
            object !== group &&
            !object.userData?.name?.includes('ACCESSORY')
          ) {
            hasNonSinkChildren = true;
          }
        });
        return hasNonSinkChildren;
      });
  }

  //Tạo nẹp TOP cho module góc
  async createTopCabinetTrimForCorner(cornerModule) {
    const cloneCornerModule = cornerModule.clone();

    // Reset rotation tạm thời để tính size chính xác
    cloneCornerModule.rotation.set(0, 0, 0);
    cloneCornerModule.updateMatrixWorld(true);

    const box = new THREE.Box3().setFromObject(cloneCornerModule);
    const size = new THREE.Vector3();
    box.getSize(size);

    // Tìm tất cả mesh có tên chứa "NEP-NOC"
    const cabinetTrimTopMeshes = [];
    cloneCornerModule.traverse(child => {
      if (
        child.isMesh &&
        child.userData.name &&
        child.userData.name.toUpperCase().includes('NEP-NOC')
      ) {
        cabinetTrimTopMeshes.push(child.clone());
      }
    });

    if (cabinetTrimTopMeshes.length === 0) {
      console.warn("Không tìm thấy mesh nào có tên chứa 'NEP-NOC'");
      return null;
    }

    const groupCornerTopCabinetTrim = new THREE.Group();

    const modules = Array.from(
      this.sceneManager?.cabinetManager?.currentCabinet?.modules
    );
    const result = modules.find(
      ([, value]) => value.group.uuid === cornerModule.uuid
    );
    let texture;
    let textureDetail;
    try {
      if (result) {
        const [, value] = result;
        const frameTexture = value?.textures?.get('frame');
        const doorTexture = value?.textures?.get('DOOR');

        if (
          doorTexture?.textureUrl &&
          !doorTexture?.material?.toLowerCase()?.includes('aluminum')
        ) {
          texture = await this.loadTexture(doorTexture.textureUrl, 0, {
            x: 1,
            y: 10,
          });

          textureDetail = doorTexture;
        } else if (frameTexture) {
          texture = await this.loadTexture(frameTexture.textureUrl, 0, {
            x: 1,
            y: 10,
          });

          textureDetail = frameTexture;
        }
      }
    } catch (err) {
      console.error('Failed to load door texture:', err);
    }

    // Material cho nẹp nóc
    const material = new THREE.MeshStandardMaterial({
      color: !texture ? this.cabinetTrimColor : null,
      map: texture,
      opacity: 1,
      transparent: true,
      side: THREE.DoubleSide,
      roughness: 1,
      metalness: 0.0,
      flatShading: false,
    });

    cabinetTrimTopMeshes.forEach(mesh => {
      const cbTrimTop = mesh.clone();

      const meshBox = new THREE.Box3().setFromObject(cloneCornerModule);
      const meshSize = new THREE.Vector3();
      meshBox.getSize(meshSize);

      // countertop.material = mesh.material.clone(); // Clone material để tránh chia sẻ
      cbTrimTop.visible = true;

      cbTrimTop.position.copy(mesh.position);
      cbTrimTop.position.setX(cbTrimTop.position.x - size.x / 2);
      cbTrimTop.position.setY(cbTrimTop.position.y + this.heightCabinetTrim);
      cbTrimTop.position.setZ(cbTrimTop.position.z + size.z / 2);

      // Thay đổi màu sắc để phân biệt (tùy chọn)
      if (cbTrimTop.material) {
        cbTrimTop.material = material;
      }

      const checkUseAcrylic = textureDetail?.supplier?.materialType
        ?.toLowerCase()
        .includes('acrylic');

      cbTrimTop.name = 'GROUP_TOP_CBTRIM_CORNER';
      cbTrimTop.userData = {
        type: 'cabinetTrim',
        name: 'GROUP_TOP_CBTRIM_CORNER',
        useAcrylic: checkUseAcrylic,
        ...(textureDetail ?? {}),
      };

      // Thêm vào group
      groupCornerTopCabinetTrim.add(cbTrimTop);
    });

    groupCornerTopCabinetTrim.position.copy(cornerModule.position);
    groupCornerTopCabinetTrim.rotation.copy(cornerModule.rotation);

    this.sceneManager.scene.add(groupCornerTopCabinetTrim);

    return groupCornerTopCabinetTrim;
  }

  // Tạo đủ nẹp TOP cho nhóm modules
  async createTopCabinetTrimForGroup(groupId, moduleGroup) {
    // Tạo biến mới loại bỏ các phụ kiện khỏi từng group
    const moduleGroupWithoutAccessory =
      this.removeAccessoriesFromModelGroup(moduleGroup);

    let hasDoor = false;
    try {
      moduleGroup[0].traverse(obj => {
        if (obj?.userData?.type === InsideType.DOOR) {
          hasDoor = true;

          throw new Error('found');
        }
      });
    } catch {}

    //Thêm nẹp nóc cho module góc
    if (
      moduleGroup?.length === 1 &&
      (moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('corner') ||
        moduleGroup[0]?.userData?.moduleData?.moduleType?.includes('curve'))
    ) {
      const cornerCountertop = this.createTopCabinetTrimForCorner(
        moduleGroup[0]
      );

      this.lstTopCabinetTrim.set(groupId, {
        plane: cornerCountertop,
        modules: moduleGroup,
      });

      // moduleGroup.forEach((module) => {
      //   this.moduleGroups.set(module.uuid, groupId);
      // });

      return cornerCountertop;
    }

    const combinedBBox = this.calculateGroupBoundingBox(
      moduleGroupWithoutAccessory
    );
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    combinedBBox.getSize(size);
    combinedBBox.getCenter(center);

    // Xác định hướng chính của group dựa trên module đầu tiên trong group
    const orientation = GeometryUtils.getModuleOrientation(moduleGroup[0]);

    // Tạo geometry và position dựa trên orientation
    const { geometry, position } =
      this.createMeshGeometryAndPositionForCabinetTrim(
        null,
        CabinetTrimPos.TOP,
        orientation,
        size,
        center,
        combinedBBox,
        this.widthCabinetTrim,
        this.heightCabinetTrim,
        this.thickness,
        hasDoor
      );

    const modules = Array.from(
      this.sceneManager?.cabinetManager?.currentCabinet?.modules
    );
    const result = modules.find(([, value]) =>
      moduleGroup.some(item => item.uuid === value.group.uuid)
    );
    let texture;
    let textureDetail;
    try {
      if (result) {
        const [, value] = result;
        const frameTexture = value?.textures?.get('frame');
        const doorTexture = value?.textures?.get('DOOR');

        if (
          doorTexture?.textureUrl &&
          !doorTexture?.material?.toLowerCase()?.includes('aluminum')
        ) {
          texture = await this.loadTexture(doorTexture.textureUrl, 0, {
            x: 1,
            y: 0.25,
          });
          textureDetail = doorTexture;
        } else if (frameTexture) {
          texture = await this.loadTexture(frameTexture.textureUrl, 0, {
            x: 1,
            y: 0.25,
          });
          textureDetail = frameTexture;
        }
      }
    } catch (err) {
      console.error('Failed to load door texture:', err);
    }

    // Material cho nẹp nóc
    const material = new THREE.MeshStandardMaterial({
      color: !texture ? this.cabinetTrimColor : null,
      map: texture,
      opacity: 1,
      transparent: true,
      side: THREE.DoubleSide,
      roughness: 1,
      metalness: 0.0,
      flatShading: false,
    });

    const groupTopCBTrim = new THREE.Mesh(geometry, material);
    groupTopCBTrim.position.copy(position);

    const checkUseAcrylic = textureDetail?.supplier?.materialType
      ?.toLowerCase()
      .includes('acrylic');

    groupTopCBTrim.name = 'GROUP_TOP_CBTRIM';
    groupTopCBTrim.userData = {
      type: 'cabinetTrim',
      groupId: groupId,
      moduleIds: moduleGroup.map(md => md.uuid),
      name: 'GROUP_TOP_CBTRIM',
      orientation: orientation,
      useAcrylic: checkUseAcrylic,
      ...(textureDetail ?? {}),
    };

    // Thêm nẹp nóc vào scene
    this.sceneManager.scene.add(groupTopCBTrim);

    // Lưu thông tin nẹp nóc vào Map
    this.lstTopCabinetTrim.set(groupId, {
      plane: groupTopCBTrim,
      modules: moduleGroup,
      boundingBox: combinedBBox.clone(),
      orientation: orientation,
    });

    return groupTopCBTrim;
  }

  // Tạo nẹp viền cho từng module tiếp xúc với tường sử dụng logic chung cho mọi góc quay
  createEdgeCabinetTrimForModule(id, module) {
    // Loại bỏ phụ kiện
    const moduleWithoutAccessory = GeometryUtils.moduleOnlyFrame(module);

    let hasDoor = false;
    try {
      module.traverse(obj => {
        if (obj?.userData?.type === InsideType.DOOR) {
          hasDoor = true;

          throw new Error('found');
        }
      });
    } catch {}

    //Nẹp đôn ngồi (đặc biệt)
    if (
      module.userData.moduleType?.includes('bench') ||
      module.userData.moduleType?.includes('tv')
    ) {
      hasDoor = false;
    }

    const moduleBBox = new THREE.Box3().setFromObject(moduleWithoutAccessory);
    const size = new THREE.Vector3();
    const center = new THREE.Vector3();

    moduleBBox.getSize(size);
    moduleBBox.getCenter(center);

    // Xác định orientation của group
    const orientation = GeometryUtils.getModuleOrientation(module);

    // Tạo group chứa tất cả các trim
    const trimsGroup = new THREE.Group();

    // Danh sách loại trim cần tạo
    const trimTypes = [];
    if (module.userData.cabinetTrimPos.left) {
      trimTypes.push(CabinetTrimPos.LEFT);
    }
    if (module.userData.cabinetTrimPos.right) {
      trimTypes.push(CabinetTrimPos.RIGHT);
    }
    if (
      module.userData.cabinetTrimPos.leftmost &&
      module.userData.cabinetTrimPos.left !== true
    ) {
      trimTypes.push(CabinetTrimPos.LEFTMOST);
    }
    if (
      module.userData.cabinetTrimPos.rightmost &&
      module.userData.cabinetTrimPos.right !== true
    ) {
      trimTypes.push(CabinetTrimPos.RIGHTMOST);
    }

    trimTypes.forEach(async trimType => {
      const modules = Array.from(
        this.sceneManager?.cabinetManager?.currentCabinet?.modules
      );
      const result = modules.find(
        ([, value]) => value.group.uuid === module.uuid
      );
      let texture;
      let textureDetail;
      try {
        if (result) {
          const [, value] = result;
          const frameTexture = value?.textures?.get('frame');
          const doorTexture = value?.textures?.get('DOOR');

          const rotationTexture =
            trimType === CabinetTrimPos.LEFT ||
            trimType === CabinetTrimPos.RIGHT
              ? Math.PI / 2
              : 0;
          const repeatTexture = { x: 1, y: 0.25 };

          if (
            [CabinetTrimPos.LEFTMOST, CabinetTrimPos.RIGHTMOST].includes(
              trimType
            )
          ) {
            //Nếu là nẹp bìa nóc cho module cuối thì lấy theo màu của khung
            if (frameTexture) {
              texture = await this.loadTexture(
                frameTexture.textureUrl,
                rotationTexture,
                repeatTexture
              );
              textureDetail = frameTexture;
            }
          } else {
            //Nếu là nẹp bìa/nẹp nóc bình thường thì lấy theo màu của cửa (nếu kh phải cửa nhôm), nếu kh có cửa thì mới lấy theo màu khung
            if (
              doorTexture?.textureUrl &&
              !doorTexture?.material?.toLowerCase()?.includes('aluminum')
            ) {
              texture = await this.loadTexture(
                doorTexture.textureUrl,
                rotationTexture,
                repeatTexture
              );
              textureDetail = doorTexture;
            } else if (frameTexture) {
              texture = await this.loadTexture(
                frameTexture.textureUrl,
                rotationTexture,
                repeatTexture
              );
              textureDetail = frameTexture;
            }
          }
        }
      } catch (err) {
        console.error('Failed to load texture:', err);
      }

      const { geometry, position } =
        this.createMeshGeometryAndPositionForCabinetTrim(
          module.userData.cabinetTrimPos,
          trimType,
          orientation,
          size,
          center,
          moduleBBox,
          this.widthCabinetTrim,
          this.heightCabinetTrim,
          this.thickness,
          hasDoor
        );

      // Material chung cho nẹp
      const trimMaterial = new THREE.MeshStandardMaterial({
        opacity: 1,
        transparent: true,
        side: THREE.DoubleSide,
        roughness: 1,
        metalness: 0.0,
        flatShading: false,
        color: !texture ? this.cabinetTrimColor : null,
        map: texture,
      });

      const checkUseAcrylic = textureDetail?.supplier?.materialType
        ?.toLowerCase()
        .includes('acrylic');

      const trimMesh = new THREE.Mesh(geometry, trimMaterial);
      trimMesh.position.copy(position);
      trimMesh.name = `GROUP${trimType === CabinetTrimPos.LEFTMOST || trimType === CabinetTrimPos.RIGHTMOST ? '_LAST' : '_EDGE'}_CBTRIM`;
      trimMesh.userData = {
        type: 'cabinetTrim',
        groupId: id,
        trimType: trimType,
        moduleId: module.uuid,
        // name: 'GROUP_EDGE_CBTRIM',
        name: `GROUP${trimType === CabinetTrimPos.LEFTMOST || trimType === CabinetTrimPos.RIGHTMOST ? '_LAST' : '_EDGE'}_CBTRIM`,
        orientation: orientation,
        useAcrylic: checkUseAcrylic,
        ...(textureDetail ?? {}),
      };

      trimsGroup.add(trimMesh);
    });

    // Add group vào scene
    this.sceneManager.scene.add(trimsGroup);

    // Lưu thông tin vào Map
    this.lstEdgeCabinetTrim.set(id, {
      plane: trimsGroup,
      modules: module,
      boundingBox: moduleBBox.clone(),
      orientation: orientation,
    });

    return trimsGroup;
  }

  // Tạo ốp bìa cho từng module ở bìa sử dụng cho mọi góc quay
  createEdgeCabinetCoverForModule(id, module) {
    //Loại trừ tủ góc
    if (module.userData.moduleType.includes('corner')) {
      return;
    }

    // Loại bỏ phụ kiện
    const moduleWithoutAccessory = GeometryUtils.moduleWithoutAccessory(module);

    const mdClone = moduleWithoutAccessory.clone();
    mdClone.rotation.set(0, 0, 0); //Quay về góc mặc định để tính size

    const moduleBBoxClone = new THREE.Box3().setFromObject(mdClone);
    const size = new THREE.Vector3();
    moduleBBoxClone.getSize(size);

    const moduleBBox = new THREE.Box3().setFromObject(moduleWithoutAccessory);

    // Xác định orientation của group
    const orientation = GeometryUtils.getModuleOrientation(module);

    // Tạo group chứa tất cả các trim
    const trimsGroup = new THREE.Group();

    // Danh sách loại trim cần tạo
    const coverTypes = [];
    if (
      !module.userData.cabinetTrimPos?.left &&
      module.userData.cabinetCoverPos?.left === false
    ) {
      coverTypes.push(CabinetCoverPos.LEFT);
    }
    if (
      !module.userData.cabinetTrimPos?.right &&
      module.userData.cabinetCoverPos?.right === false
    ) {
      coverTypes.push(CabinetCoverPos.RIGHT);
    }

    coverTypes.forEach(async coverType => {
      const modules = Array.from(
        this.sceneManager?.cabinetManager?.currentCabinet?.modules
      );
      const result = modules.find(
        ([, value]) => value.group.uuid === module.uuid
      );
      let texture;
      let textureDetail;
      try {
        if (result) {
          const [, value] = result;
          const frameTexture = value?.textures?.get('frame');
          const doorTexture = value?.textures?.get('DOOR');

          if (
            doorTexture?.textureUrl &&
            !doorTexture?.material?.toLowerCase()?.includes('aluminum')
          ) {
            texture = await this.loadTexture(doorTexture.textureUrl);
            texture.rotation = Math.PI / 2;
            texture.center.set(0.5, 0.5);
            textureDetail = doorTexture;
          } else if (frameTexture) {
            texture = await this.loadTexture(frameTexture.textureUrl);
            texture.rotation = Math.PI / 2;
            texture.center.set(0.5, 0.5);
            textureDetail = frameTexture;
          }
        }
      } catch (err) {
        console.error('Failed to load door texture:', err);
      }

      const { geometry, position } =
        this.createMeshGeometryAndPositionForCabinetCover(
          module.userData.cabinetTrimPos,
          coverType,
          orientation,
          size,
          moduleBBox,
          this.thickness
        );

      // Material chung cho nẹp
      const trimMaterial = new THREE.MeshStandardMaterial({
        opacity: 1,
        transparent: true,
        side: THREE.DoubleSide,
        roughness: 1,
        metalness: 0.0,
        flatShading: false,
        color: !texture ? this.cabinetTrimColor : null,
        map: texture,
      });

      const trimMesh = new THREE.Mesh(geometry, trimMaterial);
      trimMesh.position.copy(position);
      trimMesh.name = 'GROUP_CABINET_COVER';

      const checkUseAcrylic = textureDetail?.supplier?.materialType
        ?.toLowerCase()
        .includes('acrylic');

      trimMesh.userData = {
        type: 'cabinetCover',
        groupId: id,
        coverType: coverType,
        moduleId: module.uuid,
        name: 'GROUP_CABINET_COVER',
        orientation: orientation,
        useAcrylic: checkUseAcrylic,
        ...(textureDetail ?? {}),
      };

      trimsGroup.add(trimMesh);
    });

    // Add group vào scene
    this.sceneManager.scene.add(trimsGroup);

    // Lưu thông tin vào Map
    this.lstEdgeCabinetTrim.set(id, {
      plane: trimsGroup,
      modules: module,
      boundingBox: moduleBBox.clone(),
      orientation: orientation,
    });

    return trimsGroup;
  }

  // Tạo geometry và position cho nẹp dựa trên orientation
  createMeshGeometryAndPositionForCabinetTrim(
    cabinetTrimPos,
    type,
    orientation,
    size,
    center,
    combinedBBox,
    meshWidth,
    meshHeight,
    meshThickness,
    hasDoor = true
  ) {
    let geometry, position;
    let sizeY = size.y;
    let offsetDoor = hasDoor ? 0 : meshThickness;

    if (cabinetTrimPos?.top === true) {
      sizeY += this.heightCabinetTrim;
    }

    switch (type) {
      case CabinetTrimPos.TOP:
        if (orientation === 'NORMAL') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            meshThickness, // Độ dày theo trục X
            meshHeight, // Chiều cao
            size.z // Chiều rộng theo trục Z
          );

          position = new THREE.Vector3(
            combinedBBox.max.x + meshThickness / 2 - offsetDoor, // Phía trước của group
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            center.z // Căn giữa theo trục Z
          );
        } else if (orientation === 'ROTATED_90') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(
            size.x, // Chiều rộng theo trục X
            meshHeight, // Chiều cao
            meshThickness // Độ dày theo trục Z
          );

          position = new THREE.Vector3(
            center.x, // Căn giữa theo trục X
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            combinedBBox.min.z - meshThickness / 2 + offsetDoor // Phía trước của group
          );
        } else if (orientation === 'ROTATED_180') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            meshThickness, // Độ dày theo trục X
            meshHeight, // Chiều cao
            size.z // Chiều rộng theo trục Z
          );

          position = new THREE.Vector3(
            combinedBBox.min.x - meshThickness / 2 + offsetDoor, // Phía trước của group
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            center.z // Căn giữa theo trục Z
          );
        } else if (orientation === 'ROTATED_270') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(
            size.x, // Chiều rộng theo trục X
            meshHeight, // Chiều cao
            meshThickness // Độ dày theo trục Z
          );

          position = new THREE.Vector3(
            center.x, // Căn giữa theo trục X
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            combinedBBox.max.z + meshThickness / 2 - offsetDoor // Phía trước của group
          );
        }
        break;

      case CabinetTrimPos.LEFT:
        if (orientation === 'NORMAL') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(meshThickness, sizeY, meshWidth);

          position = new THREE.Vector3(
            combinedBBox.max.x + meshThickness / 2 - offsetDoor, // Phía trước của group
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.max.z + meshWidth / 2
          );
        } else if (orientation === 'ROTATED_90') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(meshWidth, sizeY, meshThickness);

          position = new THREE.Vector3(
            combinedBBox.max.x + meshWidth / 2, // Phía trước của group
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.min.z - meshThickness / 2 + offsetDoor
          );
        } else if (orientation === 'ROTATED_180') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(meshThickness, sizeY, meshWidth);

          position = new THREE.Vector3(
            combinedBBox.min.x - meshThickness / 2 + offsetDoor, // Phía trước của group
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.min.z - meshWidth / 2
          );
        } else if (orientation === 'ROTATED_270') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(meshWidth, sizeY, meshThickness);

          position = new THREE.Vector3(
            combinedBBox.min.x - meshWidth / 2,
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.max.z + meshThickness / 2 - offsetDoor // Phía trước của group
          );
        }
        break;

      case CabinetTrimPos.RIGHT:
        if (orientation === 'NORMAL') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(meshThickness, sizeY, meshWidth);

          position = new THREE.Vector3(
            combinedBBox.max.x + meshThickness / 2 - offsetDoor, // Phía trước của group
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.min.z - meshWidth / 2
          );
        } else if (orientation === 'ROTATED_90') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(meshWidth, sizeY, meshThickness);

          position = new THREE.Vector3(
            combinedBBox.min.x - meshWidth / 2, // Phía trước của group
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.min.z - meshThickness / 2 + offsetDoor
          );
        } else if (orientation === 'ROTATED_180') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(meshThickness, sizeY, meshWidth);

          position = new THREE.Vector3(
            combinedBBox.min.x - meshThickness / 2 + offsetDoor, // Phía trước của group
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.max.z + meshWidth / 2
          );
        } else if (orientation === 'ROTATED_270') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(meshWidth, sizeY, meshThickness);

          position = new THREE.Vector3(
            combinedBBox.max.x + meshWidth / 2,
            combinedBBox.min.y + sizeY / 2,
            combinedBBox.max.z + meshThickness / 2 - offsetDoor // Phía trước của group
          );
        }
        break;

      case CabinetTrimPos.LEFTMOST:
        if (orientation === 'NORMAL') {
          geometry = new THREE.BoxGeometry(
            size.x,
            meshHeight, // Chiều cao
            meshThickness
          );

          position = new THREE.Vector3(
            center.x,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            combinedBBox.max.z - meshThickness / 2
          );
        } else if (orientation === 'ROTATED_90') {
          geometry = new THREE.BoxGeometry(
            meshThickness,
            meshHeight, // Chiều cao
            size.z
          );

          position = new THREE.Vector3(
            combinedBBox.max.x - meshThickness / 2,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            center.z
          );
        } else if (orientation === 'ROTATED_180') {
          geometry = new THREE.BoxGeometry(
            size.x,
            meshHeight, // Chiều cao
            meshThickness
          );

          position = new THREE.Vector3(
            center.x,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            combinedBBox.min.z + meshThickness / 2
          );
        } else if (orientation === 'ROTATED_270') {
          geometry = new THREE.BoxGeometry(
            meshThickness,
            meshHeight, // Chiều cao
            size.z
          );

          position = new THREE.Vector3(
            combinedBBox.min.x + meshThickness / 2,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            center.z
          );
        }
        break;

      case CabinetTrimPos.RIGHTMOST:
        if (orientation === 'NORMAL') {
          geometry = new THREE.BoxGeometry(
            size.x,
            meshHeight, // Chiều cao
            meshThickness
          );

          position = new THREE.Vector3(
            center.x,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            combinedBBox.min.z + meshThickness / 2
          );
        } else if (orientation === 'ROTATED_90') {
          geometry = new THREE.BoxGeometry(
            meshThickness,
            meshHeight, // Chiều cao
            size.z
          );

          position = new THREE.Vector3(
            combinedBBox.min.x + meshThickness / 2,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            center.z
          );
        } else if (orientation === 'ROTATED_180') {
          geometry = new THREE.BoxGeometry(
            size.x,
            meshHeight, // Chiều cao
            meshThickness
          );

          position = new THREE.Vector3(
            center.x,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            combinedBBox.max.z - meshThickness / 2
          );
        } else if (orientation === 'ROTATED_270') {
          geometry = new THREE.BoxGeometry(
            meshThickness,
            meshHeight, // Chiều cao
            size.z
          );

          position = new THREE.Vector3(
            combinedBBox.max.x - meshThickness / 2,
            combinedBBox.max.y + meshHeight / 2, //Trên nóc
            center.z
          );
        }
        break;

      default:
        break;
    }

    return { geometry, position };
  }

  // Tạo geometry và position cho ốp bìa tủ dựa trên orientation
  createMeshGeometryAndPositionForCabinetCover(
    cabinetTrimPos,
    type,
    orientation,
    size,
    combinedBBox,
    meshThickness
  ) {
    let geometry, position;
    let sizeHeight = size.y;
    const sizeDepth = size.x + this.thickness;

    if (cabinetTrimPos?.top === true) {
      sizeHeight += this.heightCabinetTrim;
    }

    switch (type) {
      case CabinetCoverPos.LEFT:
        if (orientation === 'NORMAL') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(
            sizeDepth,
            sizeHeight,
            meshThickness
          );

          position = new THREE.Vector3(
            combinedBBox.min.x + sizeDepth / 2, // Phía sau của group
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.max.z + meshThickness / 2
          );
        } else if (orientation === 'ROTATED_90') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            meshThickness,
            sizeHeight,
            sizeDepth
          );

          position = new THREE.Vector3(
            combinedBBox.max.x + meshThickness / 2,
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.max.z - sizeDepth / 2 // Phía sau của group
          );
        } else if (orientation === 'ROTATED_180') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            sizeDepth,
            sizeHeight,
            meshThickness
          );

          position = new THREE.Vector3(
            combinedBBox.max.x - sizeDepth / 2, // Phía sau của group
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.min.z - meshThickness / 2
          );
        } else if (orientation === 'ROTATED_270') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(
            meshThickness,
            sizeHeight,
            sizeDepth
          );

          position = new THREE.Vector3(
            combinedBBox.min.x - meshThickness / 2,
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.min.z + sizeDepth / 2 // Phía sau của group
          );
        }
        break;

      case CabinetCoverPos.RIGHT:
        if (orientation === 'NORMAL') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            sizeDepth,
            sizeHeight,
            meshThickness
          );

          position = new THREE.Vector3(
            combinedBBox.min.x + sizeDepth / 2, // Phía sau của group
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.min.z - meshThickness / 2
          );
        } else if (orientation === 'ROTATED_90') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            meshThickness,
            sizeHeight,
            sizeDepth
          );

          position = new THREE.Vector3(
            combinedBBox.min.x - meshThickness / 2,
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.max.z - sizeDepth / 2 // Phía sau của group
          );
        } else if (orientation === 'ROTATED_180') {
          // Group kéo dài theo trục Z - vách bếp đặt song song với trục Z
          geometry = new THREE.BoxGeometry(
            sizeDepth,
            sizeHeight,
            meshThickness
          );

          position = new THREE.Vector3(
            combinedBBox.max.x - sizeDepth / 2, // Phía sau của group
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.max.z + meshThickness / 2
          );
        } else if (orientation === 'ROTATED_270') {
          // Group kéo dài theo trục X - vách bếp đặt song song với trục X
          geometry = new THREE.BoxGeometry(
            meshThickness,
            sizeHeight,
            sizeDepth
          );

          position = new THREE.Vector3(
            combinedBBox.max.x + meshThickness / 2,
            combinedBBox.min.y + sizeHeight / 2,
            combinedBBox.min.z + sizeDepth / 2 // Phía sau của group
          );
        }
        break;

      default:
        break;
    }

    return { geometry, position };
  }

  // Tạo tất cả group planes với validation nghiêm ngặt
  async createAllCabinetTrim() {
    // const allModules = this.getAllModules(['corner', 'bench']);
    // const allModules = this.getAllModules(['bench']);
    const allModules = this.getAllModules();
    if (allModules.length === 0) {
      return [];
    }

    // const moduleGroups = this.findModuleGroups(allModules, "ceiling");
    const moduleGroups = this.findCeilingModuleGroups(allModules);

    const createdPlanes = [];

    // Tạo nẹp nóc theo group modle
    for (let i = 0; i < moduleGroups.length; i++) {
      const group = moduleGroups[i];

      // Validate group với điều kiện nghiêm ngặt
      if (this.validateGroup(group)) {
        const groupPlaneId = `planeGroup_${i}_${Date.now()}`;

        try {
          const groupPlane = this.createTopCabinetTrimForGroup(
            groupPlaneId,
            group
          );
          createdPlanes.push(groupPlane);
        } catch (error) {
          console.error(`Failed to create group`, error);
        }
      } else {
        console.log(`Group with ${group.length} modules failed validation`);
      }
    }

    //Tạo nẹp viền (chỉ module nào sát tường mới có nẹp viền)
    // Validate và tạo group planes
    for (let i = 0; i < allModules.length; i++) {
      const edgeCBTrimId = `edgeCabinetTrim_${i}_${Date.now()}`;
      const md = allModules[i];

      if (md.userData?.cabinetTrimPos) {
        try {
          const groupCabinetTrim = this.createEdgeCabinetTrimForModule(
            edgeCBTrimId,
            md
          );
          createdPlanes.push(groupCabinetTrim);
        } catch (error) {
          console.error(`Failed to create group`, error);
        }
      }
    }

    return createdPlanes;
  }

  async createAllCabinetCover() {
    //Truyền vào những module kh cần thêm ốp bìa
    const allModules = this.getAllModules([
      'corner',
      'totl',
      'cabinet_dry',
      'dotke',
      'tamopbia',
    ]);
    if (allModules.length === 0) {
      return [];
    }

    const createdPlanes = [];

    //Tạo nẹp viền (chỉ module nào sát tường mới có nẹp viền)
    // Validate và tạo group planes
    for (let i = 0; i < allModules.length; i++) {
      const edgeCBTrimId = `edgeCabinetCover_${i}_${Date.now()}`;
      const md = allModules[i];

      if (md.userData?.cabinetCoverPos) {
        try {
          const groupCabinetCover = this.createEdgeCabinetCoverForModule(
            edgeCBTrimId,
            md
          );
          createdPlanes.push(groupCabinetCover);
        } catch (error) {
          console.error(`Failed to create group`, error);
        }
      }
    }

    return createdPlanes;
  }

  // Cập nhật khi có thay đổi
  async updateCabinetTrim() {
    //Tạo lại tất cả nẹp
    await this.createAllCabinetTrim();
  }

  // Cập nhật khi có thay đổi
  async updateCabinetCover() {
    //Tạo lại tất cả nẹp
    await this.createAllCabinetCover();
  }

  // Thiết lập các tham số
  setProximityThreshold(threshold) {
    this.proximityThreshold = threshold;
  }

  setRotationTolerance(tolerance) {
    this.rotationTolerance = tolerance;
  }

  setAlignmentTolerance(tolerance) {
    this.alignmentTolerance = tolerance;
  }

  setSizeTolerance(tolerance) {
    this.sizeTolerance = tolerance;
  }

  setPlanePadding(padding) {
    this.planePadding = padding;
  }

  setPlaneHeight(height) {
    this.planeHeight = height;
  }

  setPlaneColor(color) {
    this.cabinetTrimColor = color;
  }

  // Utilities
  getModuleGroup(moduleUuid) {
    return this.moduleGroups.get(moduleUuid);
  }

  getAllGroupPlanes() {
    return Array.from(this.lstTopCabinetTrim.values()).map(data => data.plane);
  }

  getGroupInfo() {
    const info = [];
    this.lstTopCabinetTrim.forEach((data, groupId) => {
      info.push({
        groupId: groupId,
        moduleCount: data.modules.length,
        moduleIds: data.modules.map(md => md.uuid),
        planePosition: data.plane.position.clone(),
        boundingBox: data.boundingBox.clone(),
      });
    });
    return info;
  }

  // Dọn dẹp
  dispose() {
    this.lstTopCabinetTrim.clear();
    this.moduleGroups.clear();
    this.hiddenCountertops.clear();
    this.sceneManager = null;
  }
}
